/** Лига и друзья. */
import { Coins, PosInt, UInt, UnixMs } from "../../common";
import { Empty } from "../errors";
import type { EndpointDef } from "../endpoint";
import { PlayerState } from "../player";
import { z } from "zod";

/** Строка таблицы лиги или топа друзей. Других данных о чужом игроке клиент не получает. */
export const LeagueRow = z.object({ place: PosInt, name: z.string(), points: UInt, me: z.boolean() });
export type LeagueRow = z.infer<typeof LeagueRow>;

export const LeagueOutcome = z.enum(["up", "stay", "down"]);
export type LeagueOutcome = z.infer<typeof LeagueOutcome>;

/** Итог закрытой недели. */
export const LeagueResult = z.object({
  tier: UInt,
  place: PosInt,
  size: PosInt,
  outcome: LeagueOutcome,
  crystals: UInt,
});
export type LeagueResult = z.infer<typeof LeagueResult>;

export const LeagueResponse = z.object({
  tier: UInt,
  endsAt: UnixMs,
  /** Группа этой недели; `null` — очков на неделе ещё нет, игрока никуда не сажали. */
  group: z.object({ rows: z.array(LeagueRow), up: UInt, down: UInt }).nullable(),
  /** Последняя закрытая неделя игрока. */
  last: LeagueResult.nullable(),
  unclaimed: UInt,
});
export type LeagueResponse = z.infer<typeof LeagueResponse>;

export const ClaimLeagueResponse = z.object({ crystals: UInt, state: PlayerState });

/** Награда за друга глазами игрока. */
export const FriendRewardInfo = z.object({
  /** Волна, которую должен пройти новичок. */
  wave: PosInt,
  /** Монет каждому при лучшей волне этого игрока. */
  coins: Coins,
  /** Сколько наград пригласившего уже получено. */
  earned: UInt,
  /** Наград пригласившему за всё время. */
  cap: PosInt,
});
export type FriendRewardInfo = z.infer<typeof FriendRewardInfo>;

export const FriendsResponse = z.object({
  /** Я и друзья по очкам недели. */
  rows: z.array(LeagueRow),
  /** Ссылка-приглашение; `null` — у сервера нет имени бота. */
  inviteUrl: z.string().nullable(),
  /** `null` — в этой версии баланса награды нет. */
  reward: FriendRewardInfo.nullable().default(null),
});
export type FriendsResponse = z.infer<typeof FriendsResponse>;

export const leagueEndpoints = {
  /** Группа лиги на этой неделе. Гостю `forbidden`. */
  league: { method: "GET", path: "/api/league", request: Empty, response: LeagueResponse, auth: true },
  /** Забрать кристаллы за закрытые недели. */
  claimLeague: {
    method: "POST",
    path: "/api/league/claim",
    request: Empty,
    response: ClaimLeagueResponse,
    auth: true,
  },
  /** Топ друзей и ссылка-приглашение. Гостю `forbidden`. */
  friends: { method: "GET", path: "/api/friends", request: Empty, response: FriendsResponse, auth: true },
} as const satisfies Record<string, EndpointDef>;
